import _ from "lodash";
import { channelNameToType, RootRoute } from "@app/const";
import { defaultServiceContext } from "@app/contexts";
import { Table } from "@app/page-components";
import {
    DesignChannelType,
    NodeTypeName,
    ObjectClass
} from "@app/types";
import { createUuid, renderByRoute } from "@app/utilities";
import CSVUploadAndPreviewDialog from "./CSVUploadAndPreviewDialog";

const { errorMessageService } = defaultServiceContext;

export default class LocaleImportCSV extends CSVUploadAndPreviewDialog {
    /**
    * Dialog for locale CSV upload and preview
    * @module LocaleImportCSV
    * @param {Object} props React render
    */
    constructor(props) {
        super(props);
        this.onSubmit = this.onSubmit.bind(this);
    }
    
    /**
    * Handler for clicking on the primary action button on the modal.
    * This is called from the parent component.
    */
    onSubmit() {
        const csv = { ...this.state.parsedUploadedFile };
        const channelLayer = channelNameToType[this.state.channel] || DesignChannelType.voice;
        const header = csv.meta.fields;
        const {
            knowledgeBase,
            serialized,
            timestamp
        } = window.apm;

        function isMatchingPrompt(prompt, name, wftName){
            const importModuleApp = wftName?.split(`_${prompt.name}`)[0];
            const { moduleApp } = prompt.props;

            return (
                prompt.name === name
                && ((wftName) ? moduleApp === importModuleApp : !moduleApp)
            );
        }

        /**
         * Check if the @see prompt node/object matches @see row and update its text if it does.
         * @returns {true} if @see prompt matches @see row and {false} if it does not.
         */
        const updatePromptText = (prompt, row) => {
            const {
                [header[0]]: name,
                [header[1]]: wftName,
                ...langColumns
            } = row;
            if (
                prompt.props.nodeTypeName !== NodeTypeName.prompt
                || !isMatchingPrompt(prompt, name, wftName)
            ) {
                return false;
            }

            prompt.props.config.text ??= {};
            Object.entries(langColumns).forEach(([langCode, incomingText]) => {
                const existingText = prompt.props.config.text[langCode];
                const replacedText = this.replaceMatchedVariables(incomingText, existingText);

                prompt.props.config.text[langCode] = replacedText;
                if (serialized.omniChannel.isOmniChannel) {
                    prompt.props.config.channel ??= (
                        knowledgeBase.initializePromptChannelConfig(prompt)
                    );
                    const { channel } = prompt.props.config;
                    channel[channelLayer] ??= {};
                    channel[channelLayer][langCode]= replacedText;
                }

                prompt.props.lastLocalChange = timestamp;
            });
            return true;
        };

        const blankPromptObject = knowledgeBase.getCommonElements('objects')[0].items.find((object) => {
            return object._class === ObjectClass.prompt;
        });
        const applicationPromptsObjects = serialized.components.objects.filter((object) => {
            return object.props.nodeTypeName === NodeTypeName.prompt;
        });
        const missingCommonModulePrompts = [];
        csv.data.forEach((row) => {
            serialized.components.modules.forEach((mod) => {
                mod.versions[1].flow.nodes.forEach((node) => {
                    updatePromptText(node, row);
                });
            });

            const objectFound = applicationPromptsObjects.some((object) => {
                return updatePromptText(object, row);
            });
            if (!objectFound) {
                const {
                    [header[0]]: name,
                    [header[1]]: wftName
                } = row;
                if (wftName) {
                    // Do not create prompts for common modules.
                    missingCommonModulePrompts.push({ Name: name, ['WFT Name']: wftName });
                    return;
                }
                const newPromptObject = _.cloneDeep({
                    ...blankPromptObject,
                    id: createUuid(),
                    name,
                    props: {
                        ...blankPromptObject.props,
                        createdTime: timestamp,
                        lastLocalChange: timestamp,
                        lastSavedChange: 0
                    }
                });
                updatePromptText(newPromptObject, row);
                serialized.components.objects.push(newPromptObject);
            }
        });

        if (missingCommonModulePrompts.length) {
            errorMessageService.addWarning({
                message: (
                    <>
                        <p>The following common module prompts are not contained in this design.</p>
                        <Table
                            asTable
                            data={missingCommonModulePrompts}
                        />
                    </>
                ),
                title: 'Tried to import missing common module prompts'
            });
        }

        // Update serialized and parent component props.
        window.apm.setSerializedChanged();
        
        // Update settings screen.
        // TODO: Display of no-english prompts text in imported CoMos
        // is not reflected in ModuleNodeGroup > LocalTextArea
        // See CORE-28042 (formerly DEVDES2-1304)
        renderByRoute(RootRoute.settings);

        window.apm.refreshApplicationTree();
        window.apm.notification.post('success', window.l.notification.import_success);
        w2popup.close();
    }

    /**
     * Takes an old and a new string, and a regex, and returns the new string
     * with old string's variables references.
     * Used when user imports locale transcripts from Prompts.csv.
     * TODO: dont update en-US from import.
     * @param {String} newStr Imported text
     * @param {String} oldStr Existing text 
     */
    replaceMatchedVariables(newStr, oldStr = "") {
        const regex = /\{\{[^\{]*\}/g;
        const existingVariables = oldStr.match(regex);
        const newStringArray = newStr?.split(regex) || [];
        let replacedStr = '';

        if (existingVariables && newStr !== '') {
            newStringArray.forEach((str, idx) => {
                if (idx <= existingVariables.length) {
                    replacedStr += str + (existingVariables[idx] || '');
                }
            });

            return replacedStr;
        }

        return newStr;
    }
}
